import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, ChevronRight, ExternalLink, Star, FileText, Lightbulb,
  Briefcase, Globe, FileCheck2, ShieldCheck, Landmark, Banknote, Award,
  BookOpen, ClipboardCheck, Building2, TrendingUp, ArrowRight, Sparkles,
} from "lucide-react";
import { Layout } from "../components/Layout";
import { BuilderLogo } from "../components/BuilderLogo";
import { PreviewThumb } from "../components/PreviewThumb";
import { FormationFinderModal } from "../components/FormationFinderModal";
import { resourcesLibrary, resourcesFavorites, resourcesToggleFavorite } from "../lib/api";
import { useAuth } from "../hooks/useAuth";

const SECTIONS = [
  { key: "registration", num: 1, title: "Business Registration & Name Search", icon: Search },
  { key: "domains", num: 2, title: "Domain Registration", icon: Globe },
  { key: "llc", num: 3, title: "LLC & Business Formation", icon: Building2 },
  { key: "trademark", num: 4, title: "Trademark Protection", icon: Award },
  { key: "legal-docs", num: 5, title: "Legal Documents", icon: FileText },
  { key: "ein-tax", num: 6, title: "EIN & Tax Resources", icon: Landmark },
  { key: "banking", num: 7, title: "Business Banking", icon: Banknote },
];

const STATS = [
  { icon: Briefcase, big: "20+", sub: "Business Services" },
  { icon: Globe, big: "15+", sub: "Trusted Platforms" },
  { icon: FileCheck2, big: "100+", sub: "Legal Resources" },
  { icon: ShieldCheck, big: "Trusted", sub: "Verified Providers" },
];

const GUIDES = [
  { label: "LLC vs Corporation", url: "https://www.sba.gov/business-guide/launch-your-business/choose-business-structure" },
  { label: "Choosing Your Business Structure", url: "https://www.sba.gov/business-guide/launch-your-business/choose-business-structure" },
  { label: "Registered Agent Guide", url: "https://www.northwestregisteredagent.com/registered-agent" },
  { label: "Business Licenses", url: "https://www.sba.gov/business-guide/launch-your-business/apply-licenses-permits" },
  { label: "EIN Guide", url: "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online" },
  { label: "Business Banking", url: "https://mercury.com" },
  { label: "Startup Legal Checklist", url: "https://www.sba.gov/business-guide/10-steps-start-your-business" },
];

const QUICK = [
  { label: "Business Registration Checklist", url: "https://www.sba.gov/business-guide/10-steps-start-your-business" },
  { label: "Founder Agreement Guide", url: "https://www.cooleygo.com/documents/" },
  { label: "Operating Agreement", url: "https://www.rocketlawyer.com/business-and-contracts/starting-a-business/incorporation/document/llc-operating-agreement" },
  { label: "Privacy Policy Guide", url: "https://www.termsfeed.com/privacy-policy-generator/" },
  { label: "Terms of Service Guide", url: "https://www.termsfeed.com/terms-conditions-generator/" },
  { label: "Trademark Guide", url: "https://www.uspto.gov/trademarks" },
  { label: "Business Banking Guide", url: "https://mercury.com" },
];

const WHY = [
  { icon: Building2, title: "Register Your Business", text: "Choose the legal structure that fits your startup." },
  { icon: ShieldCheck, title: "Protect Yourself", text: "Separate personal and business liability." },
  { icon: Award, title: "Build Trust", text: "Increase credibility with customers and investors." },
  { icon: TrendingUp, title: "Prepare for Growth", text: "Create a strong legal foundation for scaling." },
];

const COMING_SOON = ["Accounting Software", "Insurance", "Payroll", "Bookkeeping", "International Expansion", "Business Credit", "Funding & Grants", "Compliance Monitoring"];

export default function BusinessFormation() {
  const { user, requireAuth } = useAuth();
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [query, setQuery] = useState("");
  const [favIds, setFavIds] = useState([]);
  const [finderOpen, setFinderOpen] = useState(false);

  useEffect(() => {
    resourcesLibrary().then((d) => setTools((d.tools || []).filter((t) => t.category === "business-formation"))).catch(() => {});
    window.scrollTo(0, 0);
  }, []);
  const loadFavs = useCallback(() => { if (user) resourcesFavorites().then((d) => setFavIds(d.ids || [])).catch(() => {}); else setFavIds([]); }, [user]);
  useEffect(() => { loadFavs(); }, [loadFavs]);

  const bySection = useMemo(() => {
    const map = {};
    SECTIONS.forEach((s) => { map[s.key] = tools.filter((t) => t.section === s.key); });
    return map;
  }, [tools]);

  const searchResults = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    return tools.filter((t) => t.name.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q));
  }, [tools, query]);

  const toggleFav = (id) => {
    const on = !favIds.includes(id);
    setFavIds((p) => on ? [...p, id] : p.filter((x) => x !== id));
    const run = async () => { try { const d = await resourcesToggleFavorite(id, on); setFavIds(d.ids || []); if (on) toast.success("Saved to your favorites"); } catch { loadFavs(); } };
    user ? run() : requireAuth(run);
  };

  const Card = ({ t }) => (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 hover:shadow-[0_8px_24px_rgb(0,0,0,0.06)] transition-shadow flex flex-col" data-testid={`bf-card-${t.id}`}>
      <PreviewThumb url={t.url} name={t.name} />
      <div className="flex items-start justify-between gap-2">
        <BuilderLogo url={t.url} name={t.name} size={36} />
        <button onClick={() => toggleFav(t.id)} className="text-[#c7cbd1] hover:text-[#D4AF37]" data-testid={`bf-fav-${t.id}`} aria-label="Save">
          <Star size={16} className={favIds.includes(t.id) ? "text-[#D4AF37] fill-[#D4AF37]" : ""} />
        </button>
      </div>
      <p className="font-display font-bold text-[#0F0F0F] text-sm mt-2.5 leading-tight">{t.name}</p>
      <p className="text-xs text-[#687280] mt-1 flex-1">{t.desc}</p>
      <a href={t.url} target="_blank" rel="noopener noreferrer" className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-[#0F0F0F] text-white rounded-lg py-2 text-[11px] font-bold hover:bg-[#1c1c1c]" data-testid={`bf-visit-${t.id}`}>Visit Website <ExternalLink size={11} /></a>
    </div>
  );

  const DocCard = ({ t }) => (
    <a href={t.url} target="_blank" rel="noopener noreferrer" className="rounded-2xl border border-gray-100 bg-white p-4 hover:shadow-[0_8px_24px_rgb(0,0,0,0.06)] hover:border-[#D4AF37]/40 transition-all flex flex-col items-center text-center group" data-testid={`bf-card-${t.id}`}>
      <button onClick={(e) => { e.preventDefault(); toggleFav(t.id); }} className="self-end text-[#c7cbd1] hover:text-[#D4AF37]" data-testid={`bf-fav-${t.id}`} aria-label="Save">
        <Star size={15} className={favIds.includes(t.id) ? "text-[#D4AF37] fill-[#D4AF37]" : ""} />
      </button>
      <span className="w-10 h-10 rounded-xl bg-[#FBF7EA] text-[#B8901F] flex items-center justify-center group-hover:scale-110 transition-transform"><FileText size={18} /></span>
      <p className="font-display font-bold text-[#0F0F0F] text-xs mt-2.5 leading-tight">{t.name}</p>
      <p className="text-[10px] text-[#9aa1ab] mt-1">{t.desc}</p>
    </a>
  );

  const SectionBlock = ({ s }) => {
    const items = bySection[s.key] || [];
    if (!items.length) return null;
    const isDocs = s.key === "legal-docs";
    return (
      <section className="mt-8" data-testid={`bf-section-${s.key}`}>
        <div className="flex items-center gap-2.5 mb-4">
          <span className="w-6 h-6 rounded-full bg-[#D4AF37] text-[#0F0F0F] text-xs font-extrabold flex items-center justify-center">{s.num}</span>
          <h2 className="font-display text-base sm:text-lg font-extrabold text-[#0F0F0F] uppercase tracking-tight">{s.title}</h2>
          <span className="text-xs text-[#9aa1ab] font-semibold">({items.length})</span>
        </div>
        <div className={`grid gap-3 ${isDocs ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"}`}>
          {items.map((t) => isDocs ? <DocCard key={t.id} t={t} /> : <Card key={t.id} t={t} />)}
        </div>
      </section>
    );
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8" data-testid="business-formation-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-[#687280] mb-5">
          <Link to="/" className="hover:text-[#0F0F0F]">Founder Resource Library</Link>
          <ChevronRight size={14} />
          <span className="text-[#0F0F0F] font-semibold">Business Formation</span>
        </div>

        {/* Hero */}
        <div className="rounded-3xl bg-gradient-to-br from-[#FBF7EA] to-white border border-[#D4AF37]/20 p-7 sm:p-9 grid lg:grid-cols-[1fr_400px] gap-6 items-center overflow-hidden" data-testid="bf-hero">
          <div>
            <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-[#8a6d1a] bg-white border border-[#D4AF37]/30 rounded px-2 py-1">Category</span>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-3 leading-[0.95]">
              <span className="text-[#0F0F0F]">Business</span><br /><span className="text-[#D4AF37]">Formation</span>
            </h1>
            <p className="text-[#687280] mt-4 max-w-xl">Register your business, protect your brand, and discover trusted tools to legally launch your startup.</p>
            <div className="relative mt-5 max-w-xl">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9aa1ab]" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search business registration, LLC providers, trademarks, legal documents..." className="w-full rounded-full border border-gray-200 pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37] shadow-[0_4px_20px_rgb(0,0,0,0.04)]" data-testid="bf-search" />
            </div>
          </div>
          <img src="/assets/business-formation-hero.jpg" alt="Business formation" className="w-full max-w-[400px] mx-auto object-contain" data-testid="bf-hero-img" />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-5" data-testid="bf-stats">
          {STATS.map((s, i) => (
            <div key={i} className="rounded-2xl border border-gray-100 bg-white p-4 flex items-center gap-3">
              <span className="w-11 h-11 rounded-full bg-[#FBF7EA] text-[#B8901F] flex items-center justify-center shrink-0"><s.icon size={19} /></span>
              <div><p className="font-display text-xl font-extrabold text-[#0F0F0F] leading-none">{s.big}</p><p className="text-xs text-[#687280] mt-1">{s.sub}</p></div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_300px] gap-8 mt-6">
          {/* Main sections */}
          <div>
            {searchResults ? (
              <section data-testid="bf-search-results">
                <h2 className="font-display text-lg font-extrabold text-[#0F0F0F] mb-4">{searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for "{query}"</h2>
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {searchResults.map((t) => t.doc ? <DocCard key={t.id} t={t} /> : <Card key={t.id} t={t} />)}
                  {searchResults.length === 0 && <p className="col-span-full text-sm text-[#9aa1ab] py-8 text-center">No resources match your search.</p>}
                </div>
              </section>
            ) : (
              SECTIONS.map((s) => <SectionBlock key={s.key} s={s} />)
            )}

            {/* Coming soon */}
            <div className="mt-10 rounded-2xl border border-dashed border-[#D4AF37]/40 bg-[#FBF7EA]/40 p-5" data-testid="bf-coming-soon">
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#8a6d1a] mb-3">Coming soon · more resources to help you grow</p>
              <div className="flex flex-wrap gap-2">
                {COMING_SOON.map((c) => (
                  <span key={c} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#687280] bg-white border border-gray-100 rounded-full px-3 py-1.5">{c}<span className="text-[9px] text-[#c7a44a]">Soon</span></span>
                ))}
              </div>
            </div>
          </div>

          {/* Right sidebar */}
          <aside className="space-y-5">
            <div className="rounded-2xl border border-gray-100 bg-white p-5" data-testid="bf-guides">
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#687280] mb-2">Popular Formation Guides</p>
              <div className="space-y-0.5">
                {GUIDES.map((g) => (
                  <a key={g.label} href={g.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-[#FBF7EA] transition-colors group">
                    <BookOpen size={14} className="text-[#B8901F] shrink-0" />
                    <span className="text-sm text-[#374151] group-hover:text-[#0F0F0F] flex-1">{g.label}</span>
                    <ChevronRight size={13} className="text-[#c7cbd1] group-hover:text-[#B8901F]" />
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-white p-5" data-testid="bf-quick">
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#687280] mb-2">Quick Resources</p>
              <div className="space-y-0.5">
                {QUICK.map((g) => (
                  <a key={g.label} href={g.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-[#FBF7EA] transition-colors group">
                    <ClipboardCheck size={14} className="text-[#B8901F] shrink-0" />
                    <span className="text-sm text-[#374151] group-hover:text-[#0F0F0F] flex-1">{g.label}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="rounded-3xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-5 relative overflow-hidden" data-testid="bf-help">
              <Lightbulb size={90} className="absolute -right-3 -bottom-3 text-[#D4AF37]/15" />
              <p className="font-display text-lg font-extrabold relative z-10">Not sure where to start?</p>
              <p className="text-sm text-white/70 mt-2 relative z-10">Answer a few questions and LaunchBS will recommend the best business formation path for you.</p>
              <button onClick={() => setFinderOpen(true)} className="mt-4 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0F0F0F] rounded-full px-5 py-2.5 text-sm font-bold hover:bg-[#c5a030] relative z-10" data-testid="bf-find-option"><Sparkles size={15} /> Find My Best Option <ArrowRight size={14} /></button>
            </div>
          </aside>
        </div>

        {/* Why business formation */}
        <div className="mt-12" data-testid="bf-why">
          <h2 className="font-display text-2xl font-extrabold text-[#0F0F0F] text-center">Why Business Formation?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-7">
            {WHY.map((w) => (
              <div key={w.title} className="rounded-2xl border border-gray-100 bg-white p-6 hover:shadow-[0_8px_24px_rgb(0,0,0,0.05)] transition-shadow">
                <span className="w-11 h-11 rounded-2xl bg-[#FBF7EA] text-[#B8901F] flex items-center justify-center"><w.icon size={20} /></span>
                <p className="font-display font-bold text-[#0F0F0F] mt-3">{w.title}</p>
                <p className="text-sm text-[#687280] mt-1.5">{w.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-[#9aa1ab] text-center mt-10 max-w-3xl mx-auto" data-testid="bf-disclaimer">
          LaunchBS recommends trusted third-party platforms and official government resources. Always review your local laws and regulations before registering your business.
        </p>
      </div>
      <FormationFinderModal open={finderOpen} onClose={() => setFinderOpen(false)} />
    </Layout>
  );
}
